import {
  BgColorsOutlined,
  DeleteOutlined,
  DollarOutlined,
  EditOutlined,
  PlusOutlined,
  TagOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  ColorPicker,
  Form,
  Input,
  InputNumber,
  Modal,
  message,
  Popconfirm,
  Row,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../config/api';
import { useAuthStore } from '../store/authStore';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface TicketTemplate {
  id: string;
  name: string;
  description?: string;
  defaultPrice: number;
  icon?: string;
  color?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  organization?: {
    id: string;
    name: string;
  };
  _count?: {
    ticketTypes: number;
  };
}

const TicketTemplates: React.FC = () => {
  const [templates, setTemplates] = useState<TicketTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TicketTemplate | null>(null);
  const [form] = Form.useForm();
  const { user } = useAuthStore();
  const { t } = useTranslation();

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const response = await api.get('/ticket-templates?includeInactive=true');
      setTemplates(response.data || []);
    } catch (error) {
      message.error(t('ticketTemplates.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTemplate(null);
    form.resetFields();
    form.setFieldsValue({
      isActive: true,
      defaultPrice: 50,
      color: '#1890ff',
    });
    setModalVisible(true);
  };

  const handleEdit = (record: TicketTemplate) => {
    setEditingTemplate(record);
    form.setFieldsValue(record);
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/ticket-templates/${id}`);
      message.success(t('ticketTemplates.deleted'));
      fetchTemplates();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('ticketTemplates.deleteFailed'));
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      // Convert color if it's an object from ColorPicker
      if (values.color && typeof values.color === 'object') {
        values.color = values.color.toHexString();
      }

      if (editingTemplate) {
        await api.patch(`/ticket-templates/${editingTemplate.id}`, values);
        message.success(t('ticketTemplates.updated'));
      } else {
        await api.post('/ticket-templates', values);
        message.success(t('ticketTemplates.created'));
      }

      setModalVisible(false);
      fetchTemplates();
    } catch (error: any) {
      message.error(error.response?.data?.message || t('ticketTemplates.operationFailed'));
    }
  };

  const columns: ColumnsType<TicketTemplate> = [
    {
      title: t('common.name'),
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <div
            style={{
              width: 24,
              height: 24,
              backgroundColor: record.color || '#ccc',
              borderRadius: 4,
            }}
          />
          <Text strong>{text}</Text>
        </Space>
      ),
    },
    {
      title: t('common.description'),
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
    },
    {
      title: t('ticketTemplates.columnDefaultPrice'),
      dataIndex: 'defaultPrice',
      key: 'defaultPrice',
      render: (price) => (
        <Space>
          <DollarOutlined />
          <Text>€{price.toFixed(2)}</Text>
        </Space>
      ),
    },
    ...(user?.role === 'SUPER_ADMIN'
      ? [
          {
            title: t('ticketTemplates.columnOrganization'),
            key: 'organization',
            render: (_: any, record: TicketTemplate) => (
              <Tag color="blue">{record.organization?.name || 'N/A'}</Tag>
            ),
          },
        ]
      : []),
    {
      title: t('ticketTemplates.columnUsage'),
      key: 'usage',
      render: (_, record) => (
        <Tag color={record._count?.ticketTypes ? 'blue' : 'default'}>
          {t('ticketTemplates.usage', { count: record._count?.ticketTypes || 0 })}
        </Tag>
      ),
    },
    {
      title: t('ticketTemplates.columnStatus'),
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? t('ticketTemplates.statusActive') : t('ticketTemplates.statusInactive')}
        </Tag>
      ),
    },
    {
      title: t('common.actions'),
      key: 'action',
      render: (_, record) => (
        <Space size="small">
          <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          <Popconfirm
            title={t('common.confirm_delete')}
            onConfirm={() => handleDelete(record.id)}
            okText={t('common.yes')}
            cancelText={t('common.no')}
            disabled={record._count?.ticketTypes ? true : false}
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
              disabled={record._count?.ticketTypes ? true : false}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Nem hivatkozza semmi: az ikon-mezo egy sima szoveges input. A lista
  // egyutt marad a tobbi felirattal, hogy ne maradjon magyar szoveg a
  // fajlban, de a torleset kulon takaritas dontse el.
  const iconOptions = [
    { value: 'ticket', labelKey: 'ticketTemplates.icons.ticket' },
    { value: 'crown', labelKey: 'ticketTemplates.icons.crown' },
    { value: 'clock-circle', labelKey: 'ticketTemplates.icons.clock' },
    { value: 'book', labelKey: 'ticketTemplates.icons.book' },
    { value: 'team', labelKey: 'ticketTemplates.icons.team' },
    { value: 'wifi', labelKey: 'ticketTemplates.icons.wifi' },
    { value: 'star', labelKey: 'ticketTemplates.icons.star' },
    { value: 'gift', labelKey: 'ticketTemplates.icons.gift' },
    { value: 'heart', labelKey: 'ticketTemplates.icons.heart' },
    { value: 'rocket', labelKey: 'ticketTemplates.icons.rocket' },
  ] as const;

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2}>
            <TagOutlined style={{ marginRight: 8 }} />
            {t('ticketTemplates.title')}
          </Title>
          <Text type="secondary">{t('ticketTemplates.subtitle')}</Text>
        </Col>
        <Col>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            {t('ticketTemplates.newTemplate')}
          </Button>
        </Col>
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={templates}
          rowKey="id"
          loading={loading}
          pagination={{
            showSizeChanger: true,
            showTotal: (total) => t('ticketTemplates.paginationTotal', { count: total }),
          }}
        />
      </Card>

      <Modal
        title={editingTemplate ? t('ticketTemplates.editTitle') : t('ticketTemplates.createTitle')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label={t('common.name')}
                rules={[{ required: true, message: t('common.requiredField') }]}
              >
                <Input placeholder={t('ticketTemplates.namePlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="defaultPrice"
                label={t('ticketTemplates.defaultPriceLabel')}
                rules={[{ required: true, message: t('common.requiredField') }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} placeholder="50" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label={t('common.description')}>
            <TextArea rows={3} placeholder={t('ticketTemplates.descriptionPlaceholder')} />
          </Form.Item>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="icon" label={t('ticketTemplates.iconLabel')}>
                <Input placeholder={t('ticketTemplates.iconPlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="color" label={t('ticketTemplates.colorLabel')}>
                <Input placeholder={t('ticketTemplates.colorPlaceholder')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="isActive"
                label={t('ticketTemplates.activeLabel')}
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingTemplate ? t('ticketTemplates.save') : t('common.create')}
              </Button>
              <Button onClick={() => setModalVisible(false)}>{t('ticketTemplates.cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TicketTemplates;
